import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { buildFormat, DatelessFormat } from 'src/app/components/classification-pill/format-token';
import { AccountCapabilities, AccountV2Service, SavedSearchEstimate } from 'src/app/services/account-v2-service';
import { SessionService } from 'src/app/services/session-service';
import { localDevAccess } from 'src/app/dev-access';
import {
  alertScopeFor,
  BuilderChoices,
  BuilderFrequency,
  BuilderStyle,
  DEFAULT_CHOICES,
  fromSavedSearch,
  styleList,
  toCriteriaJson,
  toPence,
} from './saved-search-criteria';

interface LengthChip { characters: number; tokens: string[]; }

/**
 * "New saved search" / "Edit saved search" builder (plan step 4a, approved
 * canvas 2026-10-23). Dev2-only routes /account/searches/new and
 * /account/searches/:id/edit. The live panel's summary and counts come from
 * POST /account/searches/estimate so the wording matches the API exactly.
 *
 * Only the criteria, scopes and frequencies the API's alert system honours
 * (GET /account/capabilities) are offered. On production's legacy alerts that
 * is a word, optionally for one style, alerted as plates are listed, with no
 * counts (they need DEV2's listing state).
 */
@Component({
  selector: 'pla-saved-search-builder',
  templateUrl: './saved-search-builder.component.html',
  styleUrls: ['./saved-search-builder.component.scss'],
})
export class SavedSearchBuilderComponent implements OnInit, OnDestroy {
  public editId: number | null = null;
  public loading = false;
  public choices: BuilderChoices = { ...DEFAULT_CHOICES, formats: [...DEFAULT_CHOICES.formats] };

  public estimate: SavedSearchEstimate | null = null;
  public estimating = false;
  public estimateError: string | null = null;
  public saving = false;
  public saveError: string | null = null;
  /** Null until loaded; nothing is estimated or saved before then. */
  public caps: AccountCapabilities | null = null;
  /**
   * A search saved with several styles (older searches can cover, say,
   * prefix, suffix and dateless). The chips pick one style, so these are kept
   * as they are unless the member picks a different style.
   */
  public keptStyles: string[] | null = null;
  private keptStyleChoice: BuilderStyle | null = null;

  public readonly styles: { id: BuilderStyle; label: string }[] = [
    { id: 'any', label: 'Any style' },
    { id: 'dateless', label: 'Dateless' },
    { id: 'current', label: 'Current' },
    { id: 'prefix', label: 'Prefix' },
    { id: 'suffix', label: 'Suffix' },
    { id: 'ni', label: 'Northern Ireland' },
  ];

  /** Dateless formats: at most three letters (no 4-letter dateless exists). */
  public readonly formats: DatelessFormat[] = [1, 2, 3]
    .reduce((all, l) => all.concat([1, 2, 3, 4].map((n) => buildFormat(l, n))), [] as DatelessFormat[]);

  public readonly lengths: LengthChip[] = [2, 3, 4, 5, 6, 7].map((characters) => ({
    characters,
    tokens: this.formats.filter((f) => f.letters + f.numbers === characters).map((f) => f.token),
  }));

  /** Prefix and suffix registrations run 5 to 7 characters (A1 ABC to A123 ABC). */
  public readonly psLengths = [5, 6, 7];

  /** Current plates: the latest year the buyer's vehicle can carry (2001 onwards). */
  public readonly years: number[] = Array.from({ length: new Date().getFullYear() - 2000 }, (_, i) => new Date().getFullYear() - i);

  public readonly frequencies: { id: BuilderFrequency; label: string; help: string }[] = [
    { id: 'weekly', label: 'Weekly', help: 'Mondays at 7:30am. Best for most searches.' },
    { id: 'daily', label: 'Daily', help: '7:30am, only when something is new.' },
    { id: 'instant', label: 'As it happens', help: 'For searches with only a few new plates a day.' },
    { id: 'none', label: "Don't email", help: 'Keep it in your account only.' },
  ];

  private estimate$ = new Subject<string>();
  private sub: Subscription | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private session: SessionService,
    private account: AccountV2Service,
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!this.session.isLoggedIn() && !localDevAccess) {
      this.session.setSuccessRoute(id ? `/account/searches/${id}/edit` : '/account/searches/new');
      this.router.navigate(['/login']);
      return;
    }
    this.sub = this.estimate$.pipe(
      debounceTime(350),
      switchMap((criteria) => {
        this.estimating = true;
        return this.account.estimateSearch(criteria).pipe(catchError((e) => of({ error: e } as any)));
      }),
    ).subscribe((result: any) => {
      this.estimating = false;
      if (result && result.error) {
        const errors = result.error?.error?.errors;
        this.estimateError = Array.isArray(errors) && errors.length ? this.plainError(errors[0]) : "We couldn't work out the numbers just now.";
        return;
      }
      this.estimateError = null;
      this.estimate = result;
    });

    this.account.getCapabilities().subscribe({
      next: (caps) => { this.caps = caps; this.start(id); },
      error: () => (this.saveError = "We couldn't load your account just now. Try again."),
    });
  }

  private start(id: string | null): void {
    if (!id && !this.offers('formats')) this.choices.style = 'any';
    if (id) {
      this.editId = Number(id);
      this.loading = true;
      this.account.getSearches().subscribe({
        next: (searches) => {
          const found = searches.find((s) => s.id === this.editId);
          this.loading = false;
          if (!found) { this.router.navigate(['/account/searches']); return; }
          this.choices = fromSavedSearch(found.criteria, found.alertScope, found.frequency);
          const styles = styleList(found.criteria);
          if (styles.length > 1) { this.keptStyles = styles; this.keptStyleChoice = this.choices.style; }
          this.changed();
        },
        error: () => { this.loading = false; this.saveError = "We couldn't load this search just now."; },
      });
    } else {
      this.changed();
    }
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }

  // ---- what the alert system honours ----

  public offers(criterion: string): boolean {
    return !!this.caps && this.caps.searchCriteria.includes(criterion);
  }

  public get styleChoices(): { id: BuilderStyle; label: string }[] {
    return this.styles.filter((s) => s.id === 'any' || (!!this.caps && this.caps.searchStyles.includes(s.id)));
  }

  public get frequencyChoices(): { id: BuilderFrequency; label: string; help: string }[] {
    return this.frequencies.filter((f) => !!this.caps && this.caps.searchFrequencies.includes(f.id));
  }

  public get offersAuctions(): boolean {
    return !!this.caps && this.caps.searchScopes.includes('auction');
  }

  public get keepingStyles(): boolean {
    return !!this.keptStyles && this.choices.style === this.keptStyleChoice;
  }

  public get keptStylesLabel(): string {
    const labels = (this.keptStyles || []).map((id) => this.styles.find((s) => s.id === id)?.label || id);
    return labels.length > 1 ? labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1] : labels.join('');
  }

  public get counts(): boolean {
    return !!this.caps && this.caps.searchMatches;
  }

  public get termRequired(): boolean {
    return !!this.caps && this.caps.searchTermRequired;
  }

  /** The builder's criteria with anything the API doesn't honour left out. */
  private criteriaJson(): string {
    const c = JSON.parse(toCriteriaJson(this.choices));
    if (this.keepingStyles) c['style'] = this.keptStyles;
    for (const key of Object.keys(c)) if (!this.offers(key)) delete c[key];
    return JSON.stringify(c);
  }

  private request() {
    const caps = this.caps!;
    const scopes = alertScopeFor(this.choices).filter((s) => caps.searchScopes.includes(s));
    const frequency = caps.searchFrequencies.includes(this.choices.frequency) ? this.choices.frequency : caps.searchFrequencies[0];
    return {
      name: '',                      // the API names it after the plain summary
      plainSummary: '',
      criteria: this.criteriaJson(),
      alertScope: scopes.length ? scopes : [caps.searchScopes[0]],
      frequency,
    };
  }

  // ---- choices ----

  public setStyle(style: BuilderStyle): void {
    this.keptStyles = null;   // a legacy multi-style rule is replaced by the style chosen
    this.choices.style = style;
    this.changed();
  }

  /** Every kept style shows as chosen until the user picks one. */
  public styleOn(id: string): boolean {
    return this.keepingStyles ? this.keptStyles!.includes(id) : this.choices.style === id;
  }

  public isLength(chip: LengthChip): boolean {
    const f = this.choices.formats;
    return f.length === chip.tokens.length && chip.tokens.every((t) => f.includes(t));
  }

  public pickLength(chip: LengthChip): void {
    this.choices.formats = this.isLength(chip) ? [] : [...chip.tokens];
    this.changed();
  }

  public hasFormat(token: string): boolean {
    return this.choices.formats.includes(token);
  }

  public toggleFormat(token: string): void {
    this.choices.formats = this.hasFormat(token)
      ? this.choices.formats.filter((t) => t !== token)
      : [...this.choices.formats, token];
    this.changed();
  }

  public hasChars(n: number): boolean {
    return this.choices.characters.includes(n);
  }

  public toggleChars(n: number): void {
    this.choices.characters = this.hasChars(n)
      ? this.choices.characters.filter((c) => c !== n)
      : [...this.choices.characters, n];
    this.changed();
  }

  public toggleAuctions(): void {
    this.choices.includeAuctions = !this.choices.includeAuctions;
  }

  public setFrequency(f: BuilderFrequency): void {
    this.choices.frequency = f;
    this.saveError = null;
  }

  public changed(): void {
    this.saveError = null;
    if (!this.caps) return;
    const min = toPence(this.choices.minPounds), max = toPence(this.choices.maxPounds);
    if (min != null && max != null && min > max) {
      this.estimateError = 'The minimum is above the maximum.';
      return;
    }
    if (this.counts) this.estimate$.next(this.criteriaJson());
  }

  // ---- panel ----

  public get summary(): string {
    if (this.estimate?.plainSummary) return this.estimate.plainSummary;
    const term = (this.choices.term || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!term) return 'Your search';
    if (this.keepingStyles) return `${this.keptStylesLabel} plates that read as ${term}`;
    const style = this.styles.find((s) => s.id === this.choices.style);
    return this.choices.style === 'any' || !style ? `Plates that read as ${term}` : `${style.label} plates that read as ${term}`;
  }

  /** Panel note when the API keeps no counts. */
  public get alertNote(): string {
    const how = this.caps && this.caps.emailAlerts ? 'by email and as a PlateX message' : 'as a PlateX message';
    return `We alert you ${how} when a newly listed plate reads almost exactly as your word.`;
  }

  public get perWeek(): string {
    const e = this.estimate;
    if (!e || !e.available) return '–';
    if (e.newPerWeek < 1) return e.newLast28Days === 0 ? 'Under 1' : 'About 1';
    return (e.approximate ? 'About ' : '') + Math.round(e.newPerWeek).toLocaleString('en-GB');
  }

  public get forSale(): string {
    const e = this.estimate;
    if (!e || !e.available) return '–';
    return (e.approximate ? 'About ' : '') + e.currentlyListed.toLocaleString('en-GB');
  }

  public get estimateNote(): string {
    const e = this.estimate;
    if (!e) return 'Counting what matches across every plate for sale and coming up at auction.';
    if (!e.available) return "We're still building the market view these counts come from. Your search will work either way.";
    if (e.newPerWeek < 1) return 'A quiet search: most weeks nothing new will appear, and we only email when something does.';
    if (!e.instantAllowed) return 'A busy search: a daily or weekly email keeps it to one message.';
    return 'Counts cover every plate for sale and coming up at auction.';
  }

  public get instantBlocked(): boolean {
    return this.choices.frequency === 'instant' && !!this.estimate && this.estimate.available && !this.estimate.instantAllowed;
  }

  public seeMatching(): void {
    if (this.editId) this.router.navigate(['/saved-search', this.editId]);
  }

  // ---- save ----

  public save(): void {
    if (!this.caps) return;
    if (this.termRequired && !(this.choices.term || '').replace(/[^A-Za-z0-9]/g, '')) {
      this.saveError = 'Add a word, name or initials for the plate to read as.';
      return;
    }
    if (this.instantBlocked) {
      this.saveError = 'Too many new plates for as-it-happens emails. Choose daily or weekly.';
      return;
    }
    if (this.estimateError) {
      this.saveError = this.estimateError;
      return;
    }
    this.saving = true;
    this.saveError = null;
    const request = this.request();
    const call = this.editId ? this.account.updateSearch(this.editId, request) : this.account.createSearch(request);
    call.subscribe({
      next: () => this.router.navigate(['/account/searches']),
      error: (e) => {
        this.saving = false;
        const errors = e?.error?.errors;
        this.saveError = Array.isArray(errors) && errors.length ? this.plainError(errors[0]) : "We couldn't save this search just now. Try again.";
      },
    });
  }

  /** API validation messages, in the site's voice. */
  private plainError(error: string): string {
    if (error.includes('at least one criterion')) return 'Choose a style, a format, a word or a budget first.';
    if (error.includes('priceMin is above priceMax')) return 'The minimum is above the maximum.';
    const sentence = error.charAt(0).toUpperCase() + error.slice(1);
    return /[.!?]$/.test(sentence) ? sentence : sentence + '.';
  }
}
